import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchMyProfile } from '../../api/auth';
import {
  createAcceptanceInspection,
  deleteAcceptanceInspectionAttachment,
  fetchAcceptanceInspection,
  fetchAcceptanceInspections,
  fetchNextAcceptanceLotNo,
  updateAcceptanceInspection,
  uploadAcceptanceInspectionAttachment,
} from '../../api/acceptanceInspection';
import { createCompanyInventory, fetchCompanyInventory } from '../../api/companyInventory';
import { createPartVendor, fetchPartVendors } from '../../api/partVendor';
import { fetchPurchaseOrder, fetchPurchaseOrders } from '../../api/purchaseOrder';
import {
  AcceptanceInspection,
  AcceptanceInspectionAttachment,
  AcceptanceInspectionItem,
  AcceptanceInspectionPayload,
  EMPTY_ACCEPTANCE_ITEM,
  acceptanceItemLabel,
  acceptanceStatusLabel,
  isMediaAttachment,
  isRemarkAttachment,
} from '../../types/acceptanceInspection';
import { INSPECTION_FORM_TYPE_ACCEPTANCE } from '../../types/inspectionForm';
import {
  defaultStaticForm,
  listStaticForms,
  StaticInspectionForm,
} from '../../data/staticInspectionForms';
import {
  CompanyInventoryItem,
  InventoryItemKind,
  inventoryKindLabel,
} from '../../types/companyInventory';
import { PartVendor } from '../../types/partVendor';
import { PurchaseOrder, PurchaseOrderItem } from '../../types/purchaseOrder';
import AcceptanceInspectionSheet, { AcceptanceSheetValues } from './AcceptanceInspectionSheet';
import PreviousInspectionPickerModal from './PreviousInspectionPickerModal';
import { notifyError, reportError } from '../../utils/notify';
import './AcceptanceInspectionCreate.css';

interface Props {
  inspectionId?: number;
  onCancel: () => void;
  onSaved: (inspectionId: number) => void;
  /** 계약 컨텍스트에서 열면 해당 계약 발주만 표시 */
  contractId?: number | null;
}

type SheetCard = {
  key: string;
  collapsed: boolean;
  sheet: AcceptanceSheetValues;
  pendingMediaFiles: File[];
  pendingRemarkFiles: File[];
  savedAttachments: AcceptanceInspectionAttachment[];
};

function newCardKey() {
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptySheetValues(authorName = ''): AcceptanceSheetValues {
  return {
    inventoryId: null,
    itemKind: '',
    productName: '',
    authorName,
    specText: '',
    lotNo: '',
    material: '',
    incomingQty: '',
    inspectorName: authorName,
    vendorName: '',
    vendorContactName: '',
    vendorPhone: '',
    incomingDate: '',
    inspectionDate: new Date().toISOString().slice(0, 10),
    overallJudgment: '',
    remark: '',
    items: [],
  };
}

function emptyCard(authorName = '', collapsed = false): SheetCard {
  return {
    key: newCardKey(),
    collapsed,
    sheet: emptySheetValues(authorName),
    pendingMediaFiles: [],
    pendingRemarkFiles: [],
    savedAttachments: [],
  };
}

function mapFormItems(form: StaticInspectionForm | null): AcceptanceInspectionItem[] {
  if (!form || form.items.length === 0) return [];
  return form.items.map((item) => ({
    ...EMPTY_ACCEPTANCE_ITEM,
    itemName: item.itemName ?? '',
    descriptionText: item.descriptionText ?? '',
    standardText: item.standardText ?? '',
    methodText: item.methodText ?? '',
  }));
}

function mapDetailItems(
  items: AcceptanceInspection['items']
): AcceptanceInspectionItem[] {
  if (!items || items.length === 0) return [];
  return items.map((item) => ({
    itemName: item.itemName ?? '',
    descriptionText: item.descriptionText ?? '',
    standardText: item.standardText ?? '',
    methodText: item.methodText ?? '',
    sample1: item.sample1 ?? '',
    sample2: item.sample2 ?? '',
    sample3: item.sample3 ?? '',
    sample4: item.sample4 ?? '',
    sample5: item.sample5 ?? '',
    judgment: item.judgment ?? '',
  }));
}

function inventoryOptionLabel(itemName?: string | null, specText?: string | null) {
  const name = itemName?.trim() || '품명 없음';
  const spec = specText?.trim() || '기본';
  return `${name} / ${spec}`;
}

function AcceptanceInspectionCreate({
  inspectionId,
  onCancel,
  onSaved,
  contractId = null,
}: Props) {
  const isEdit = inspectionId != null;
  const forms = useMemo(() => listStaticForms(INSPECTION_FORM_TYPE_ACCEPTANCE), []);
  const [inventoryOptions, setInventoryOptions] = useState<CompanyInventoryItem[]>([]);
  const [selectedFormKey, setSelectedFormKey] = useState<string | null>(
    () => defaultStaticForm(INSPECTION_FORM_TYPE_ACCEPTANCE).key
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [previousRows, setPreviousRows] = useState<AcceptanceInspection[]>([]);
  const [itemsImportCardKey, setItemsImportCardKey] = useState<string | null>(null);
  const [loadingPreviousItems, setLoadingPreviousItems] = useState(false);
  const [showQuickRegister, setShowQuickRegister] = useState(false);
  const [quickTargetKey, setQuickTargetKey] = useState<string | null>(null);
  const [quickKind, setQuickKind] = useState<InventoryItemKind>('PART');
  const [quickName, setQuickName] = useState('');
  const [quickSpec, setQuickSpec] = useState('');
  const [quickUnit, setQuickUnit] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);
  const [vendors, setVendors] = useState<PartVendor[]>([]);
  const [showVendorQuick, setShowVendorQuick] = useState(false);
  const [vendorTargetKey, setVendorTargetKey] = useState<string | null>(null);
  const [vendorQuickName, setVendorQuickName] = useState('');
  const [vendorQuickContact, setVendorQuickContact] = useState('');
  const [vendorQuickPhone, setVendorQuickPhone] = useState('');
  const [vendorQuickSaving, setVendorQuickSaving] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState<number | null>(null);
  const [poItems, setPoItems] = useState<PurchaseOrderItem[]>([]);
  const [selectedPoKeys, setSelectedPoKeys] = useState<Set<string>>(new Set());
  const [cards, setCards] = useState<SheetCard[]>(() => [emptyCard()]);
  const [mediaPreviewByCard, setMediaPreviewByCard] = useState<
    Record<string, Record<number, string>>
  >({});
  const skipNextFormLoad = useRef(false);
  const authorNameRef = useRef('');

  const selectedForm: StaticInspectionForm | null = useMemo(
    () => forms.find((f) => f.key === selectedFormKey) ?? null,
    [forms, selectedFormKey]
  );

  const selectedPo = useMemo(
    () => purchaseOrders.find((po) => po.purchaseOrderId === selectedPurchaseOrderId) ?? null,
    [purchaseOrders, selectedPurchaseOrderId]
  );

  /** 발주별 이미 인수검사 등록된 inventoryId 집합 */
  const registeredInventoryByPo = useMemo(() => {
    const map = new Map<number, Set<number>>();
    for (const row of previousRows) {
      if (row.purchaseOrderId == null || row.inventoryId == null) continue;
      let set = map.get(row.purchaseOrderId);
      if (!set) {
        set = new Set();
        map.set(row.purchaseOrderId, set);
      }
      set.add(row.inventoryId);
    }
    return map;
  }, [previousRows]);

  const selectedPoRegisteredIds = useMemo(() => {
    if (selectedPurchaseOrderId == null) return new Set<number>();
    return registeredInventoryByPo.get(selectedPurchaseOrderId) ?? new Set<number>();
  }, [selectedPurchaseOrderId, registeredInventoryByPo]);

  const sortedPurchaseOrders = useMemo(() => {
    const rank = (po: PurchaseOrder) => {
      const registered = registeredInventoryByPo.get(po.purchaseOrderId)?.size ?? 0;
      const total =
        po.purchaseOrderId === selectedPurchaseOrderId && poItems.length > 0
          ? poItems.length
          : po.itemCount ?? 0;
      if (registered <= 0) return 0;
      if (total > 0 && registered >= total) return 2;
      return 1;
    };
    return [...purchaseOrders].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      return (b.purchaseOrderId ?? 0) - (a.purchaseOrderId ?? 0);
    });
  }, [purchaseOrders, registeredInventoryByPo, selectedPurchaseOrderId, poItems.length]);

  const itemsImportPriorityIds = useMemo(() => {
    if (itemsImportCardKey == null) return [];
    const card = cards.find((c) => c.key === itemsImportCardKey);
    const inventoryId = card?.sheet.inventoryId;
    if (inventoryId == null || inventoryId <= 0) return [];
    return previousRows
      .filter((row) => row.inventoryId === inventoryId)
      .map((row) => row.inspectionId);
  }, [itemsImportCardKey, cards, previousRows]);

  const poAcceptanceStatus = useCallback(
    (po: PurchaseOrder): { label: string; tone: 'none' | 'partial' | 'full' } => {
      const registered = registeredInventoryByPo.get(po.purchaseOrderId)?.size ?? 0;
      const total =
        po.purchaseOrderId === selectedPurchaseOrderId && poItems.length > 0
          ? poItems.length
          : po.itemCount ?? 0;
      if (registered <= 0) return { label: '미등록', tone: 'none' };
      if (total > 0 && registered >= total) return { label: '전체 등록', tone: 'full' };
      if (total > 0) return { label: `일부 등록 ${registered}/${total}`, tone: 'partial' };
      return { label: `등록 ${registered}`, tone: 'partial' };
    },
    [registeredInventoryByPo, selectedPurchaseOrderId, poItems.length]
  );

  const selectedPoAcceptance = useMemo(
    () => (selectedPo ? poAcceptanceStatus(selectedPo) : null),
    [selectedPo, poAcceptanceStatus]
  );

  useEffect(() => {
    const next: Record<string, Record<number, string>> = {};
    const created: string[] = [];
    cards.forEach((card) => {
      const urls: Record<number, string> = {};
      card.pendingMediaFiles.forEach((file, index) => {
        if (file.type.startsWith('image/')) {
          const url = URL.createObjectURL(file);
          urls[index] = url;
          created.push(url);
        }
      });
      next[card.key] = urls;
    });
    setMediaPreviewByCard(next);
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [cards]);

  const loadInventory = useCallback(async () => {
    try {
      setInventoryOptions(await fetchCompanyInventory());
    } catch {
      setInventoryOptions([]);
    }
  }, []);

  const loadVendors = useCallback(async () => {
    try {
      setVendors(await fetchPartVendors());
    } catch {
      setVendors([]);
    }
  }, []);

  const loadPurchaseOrders = useCallback(async () => {
    try {
      setPurchaseOrders(await fetchPurchaseOrders({ contractId: contractId ?? undefined }));
    } catch {
      setPurchaseOrders([]);
    }
  }, [contractId]);

  useEffect(() => {
    void loadPurchaseOrders();
  }, [loadPurchaseOrders]);

  const patchCard = (key: string, patch: Partial<SheetCard>) => {
    setCards((prev) => prev.map((card) => (card.key === key ? { ...card, ...patch } : card)));
  };

  const patchCardSheet = (key: string, patch: Partial<AcceptanceSheetValues>) => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key ? { ...card, sheet: { ...card.sheet, ...patch } } : card
      )
    );
  };

  const buildCardFromPoItem = (
    item: PurchaseOrderItem,
    po: PurchaseOrder | null,
    authorName: string,
    collapsed: boolean
  ): SheetCard => {
    const inv = inventoryOptions.find((row) => row.inventoryId === item.inventoryId);
    return {
      key: newCardKey(),
      collapsed,
      sheet: {
        ...emptySheetValues(authorName),
        inventoryId: item.inventoryId,
        itemKind: inv?.itemKind || '',
        productName: item.itemName || inv?.itemName || '',
        specText: item.specText ?? inv?.specText ?? '',
        incomingQty: item.orderQty != null ? String(item.orderQty) : '',
        vendorName: po?.vendorName || '',
        vendorContactName: po?.vendorContactName || '',
        vendorPhone: po?.vendorPhone || '',
        items: mapFormItems(selectedForm),
      },
      pendingMediaFiles: [],
      pendingRemarkFiles: [],
      savedAttachments: [],
    };
  };

  const poItemKey = (item: PurchaseOrderItem, index: number) =>
    `${item.inventoryId}-${item.itemId ?? index}`;

  const pickPurchaseOrder = async (rawId: string) => {
    if (!rawId) {
      setSelectedPurchaseOrderId(null);
      setPoItems([]);
      setSelectedPoKeys(new Set());
      if (!isEdit) {
        setCards([emptyCard(authorNameRef.current)]);
      }
      return;
    }
    const id = Number(rawId);
    setSelectedPurchaseOrderId(id);
    try {
      const detail = await fetchPurchaseOrder(id);
      const items = detail.items ?? [];
      setPoItems(items);
      if (isEdit) {
        setCards((prev) =>
          prev.map((card, index) =>
            index === 0
              ? {
                  ...card,
                  sheet: {
                    ...card.sheet,
                    vendorName: detail.vendorName || card.sheet.vendorName,
                    vendorContactName:
                      detail.vendorContactName || card.sheet.vendorContactName,
                    vendorPhone: detail.vendorPhone || card.sheet.vendorPhone,
                  },
                }
              : card
          )
        );
        return;
      }
      if (items.length === 1) {
        setSelectedPoKeys(new Set([poItemKey(items[0], 0)]));
        setCards([buildCardFromPoItem(items[0], detail, authorNameRef.current, false)]);
      } else {
        setSelectedPoKeys(new Set(items.map((item, index) => poItemKey(item, index))));
        setCards(
          items.map((item, index) =>
            buildCardFromPoItem(item, detail, authorNameRef.current, index > 0)
          )
        );
      }
    } catch {
      reportError(setError, '발주서를 불러오지 못했습니다.');
      setPoItems([]);
      setSelectedPoKeys(new Set());
    }
  };

  const togglePoItem = (key: string) => {
    setSelectedPoKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const applyPoSelectionToCards = () => {
    if (isEdit) return;
    const selected = poItems.filter((item, index) => selectedPoKeys.has(poItemKey(item, index)));
    if (selected.length === 0) {
      notifyError('발주 품목을 하나 이상 선택해 주세요.');
      return;
    }
    const po =
      purchaseOrders.find((row) => row.purchaseOrderId === selectedPurchaseOrderId) ?? null;
    setCards(
      selected.map((item, index) =>
        buildCardFromPoItem(item, po, authorNameRef.current, index > 0)
      )
    );
  };

  useEffect(() => {
    void (async () => {
      try {
        const profile = await fetchMyProfile();
        const name = profile.nickname?.trim() || profile.loginId?.trim() || '';
        authorNameRef.current = name;
        setCards((prev) =>
          prev.map((card) => ({
            ...card,
            sheet: {
              ...card.sheet,
              authorName: card.sheet.authorName || name,
              inspectorName: card.sheet.inspectorName || name,
            },
          }))
        );
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        if (isEdit) setLoading(true);
        const [history, lotSuggestion] = await Promise.all([
          fetchAcceptanceInspections(),
          isEdit ? Promise.resolve(null) : fetchNextAcceptanceLotNo().catch(() => null),
          loadInventory(),
          loadVendors(),
        ]);
        setPreviousRows(history);
        if (!isEdit && lotSuggestion) {
          // hint kept on sheet component via prop below
          void lotSuggestion;
        }

        if (isEdit && inspectionId != null) {
          const detail = await fetchAcceptanceInspection(inspectionId);
          if (detail.status === 'APPROVED') {
            notifyError('승인 완료된 인수검사는 수정할 수 없습니다.');
            return;
          }
          const matched =
            forms.find((f) => f.title === (detail.formTitle ?? '')) ?? forms[0] ?? null;
          if (matched) setSelectedFormKey(matched.key);
          setSelectedPurchaseOrderId(detail.purchaseOrderId ?? null);
          if (detail.purchaseOrderId != null) {
            try {
              const po = await fetchPurchaseOrder(detail.purchaseOrderId);
              setPoItems(po.items ?? []);
            } catch {
              setPoItems([]);
            }
          }
          setCards([
            {
              key: newCardKey(),
              collapsed: false,
              sheet: {
                ...emptySheetValues(authorNameRef.current),
                inventoryId: detail.inventoryId ?? null,
                itemKind: detail.itemKind ?? '',
                productName:
                  acceptanceItemLabel(detail) === '-' ? '' : acceptanceItemLabel(detail),
                authorName: detail.authorName || authorNameRef.current,
                reviewerName: detail.reviewerName || '',
                approverName: detail.approverName || '',
                specText: detail.specText ?? '',
                lotNo: detail.lotNo ?? '',
                material: detail.material ?? '',
                incomingQty: detail.incomingQty ?? '',
                inspectorName: detail.inspectorName ?? authorNameRef.current,
                vendorName: detail.vendorName ?? '',
                vendorContactName: detail.vendorContactName ?? '',
                vendorPhone: detail.vendorPhone ?? '',
                incomingDate: detail.incomingDate?.slice(0, 10) ?? '',
                inspectionDate:
                  detail.inspectionDate?.slice(0, 10) ??
                  new Date().toISOString().slice(0, 10),
                overallJudgment: detail.overallJudgment ?? '',
                remark: detail.remark ?? '',
                items: mapDetailItems(detail.items),
              },
              pendingMediaFiles: [],
              pendingRemarkFiles: [],
              savedAttachments: detail.attachments ?? [],
            },
          ]);
        }
        setError('');
      } catch {
        reportError(
          setError,
          isEdit
            ? '인수검사 수정 정보를 불러오지 못했습니다.'
            : '인수검사 등록 정보를 불러오지 못했습니다.'
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [forms, inspectionId, isEdit, loadInventory, loadVendors]);

  useEffect(() => {
    if (isEdit) return;
    if (!selectedForm) return;
    if (skipNextFormLoad.current) {
      skipNextFormLoad.current = false;
      return;
    }
    const nextItems = mapFormItems(selectedForm);
    setCards((prev) =>
      prev.map((card) => ({
        ...card,
        sheet: { ...card.sheet, items: nextItems },
      }))
    );
  }, [selectedForm, isEdit]);

  const [lotNoHint, setLotNoHint] = useState(
    '저장 시 자동 발급됩니다. (예: 2026-ACC-1)'
  );

  useEffect(() => {
    if (isEdit) return;
    void fetchNextAcceptanceLotNo()
      .then((lotSuggestion) => {
        const suggested = lotSuggestion.suggestedValue?.trim() || '';
        setLotNoHint(
          suggested
            ? `저장 시 자동 발급됩니다. (예: ${suggested})`
            : '저장 시 자동 발급됩니다. (예: 2026-ACC-1)'
        );
      })
      .catch(() => undefined);
  }, [isEdit]);

  const addCard = () => {
    if (isEdit) return;
    setCards((prev) => {
      const next = prev.map((card) => ({ ...card, collapsed: true }));
      return [
        ...next,
        {
          ...emptyCard(authorNameRef.current, false),
          sheet: {
            ...emptySheetValues(authorNameRef.current),
            vendorName: selectedPo?.vendorName || '',
            vendorContactName: selectedPo?.vendorContactName || '',
            vendorPhone: selectedPo?.vendorPhone || '',
            items: mapFormItems(selectedForm),
          },
        },
      ];
    });
  };

  const removeCard = (key: string) => {
    if (isEdit) return;
    setCards((prev) => {
      if (prev.length <= 1) {
        notifyError('성적서는 최소 1장이 필요합니다.');
        return prev;
      }
      return prev.filter((card) => card.key !== key);
    });
  };

  const updateItem = (key: string, index: number, patch: Partial<AcceptanceInspectionItem>) => {
    setCards((prev) =>
      prev.map((card) => {
        if (card.key !== key) return card;
        return {
          ...card,
          sheet: {
            ...card.sheet,
            items: card.sheet.items.map((row, i) => (i === index ? { ...row, ...patch } : row)),
          },
        };
      })
    );
  };

  const addItem = (key: string) => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key
          ? {
              ...card,
              sheet: {
                ...card.sheet,
                items: [...card.sheet.items, { ...EMPTY_ACCEPTANCE_ITEM }],
              },
            }
          : card
      )
    );
  };

  const removeItem = (key: string, index: number) => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key
          ? {
              ...card,
              sheet: {
                ...card.sheet,
                items: card.sheet.items.filter((_, i) => i !== index),
              },
            }
          : card
      )
    );
  };

  const applyPreviousItems = async (sourceInspectionId: number) => {
    if (itemsImportCardKey == null) return;
    try {
      setLoadingPreviousItems(true);
      setError('');
      const detail = await fetchAcceptanceInspection(sourceInspectionId);
      skipNextFormLoad.current = true;
      const items = mapDetailItems(detail.items);
      patchCardSheet(itemsImportCardKey, { items });
      setItemsImportCardKey(null);
    } catch {
      reportError(setError, '이전 검사항목을 불러오지 못했습니다.');
    } finally {
      setLoadingPreviousItems(false);
    }
  };

  const handleQuickRegister = async () => {
    if (!quickName.trim()) {
      notifyError('품목명을 입력해 주세요.');
      return;
    }
    const targetKey = quickTargetKey ?? cards[0]?.key;
    if (!targetKey) return;
    try {
      setQuickSaving(true);
      setError('');
      const created = await createCompanyInventory({
        itemKind: quickKind,
        itemName: quickName.trim(),
        specText: quickSpec.trim(),
        stockQty: 0,
        unit: quickUnit.trim() || undefined,
      });
      await loadInventory();
      patchCardSheet(targetKey, {
        inventoryId: created.inventoryId,
        itemKind: created.itemKind,
        productName: created.itemName,
        specText: created.specText ?? '',
      });
      setShowQuickRegister(false);
      setQuickTargetKey(null);
      setQuickName('');
      setQuickSpec('');
      setQuickUnit('');
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '품목 등록에 실패했습니다.'
      );
    } finally {
      setQuickSaving(false);
    }
  };

  const applyVendor = (key: string, vendor: PartVendor) => {
    patchCardSheet(key, {
      vendorName: vendor.vendorName || '',
      vendorContactName: vendor.contactName || '',
      vendorPhone: vendor.mobile?.trim() || vendor.phone?.trim() || '',
    });
  };

  const handleVendorQuickRegister = async () => {
    if (!vendorQuickName.trim()) {
      notifyError('업체명을 입력해 주세요.');
      return;
    }
    const targetKey = vendorTargetKey ?? cards[0]?.key;
    if (!targetKey) return;
    try {
      setVendorQuickSaving(true);
      setError('');
      const created = await createPartVendor({
        vendorName: vendorQuickName.trim(),
        contactName: vendorQuickContact.trim() || undefined,
        phone: vendorQuickPhone.trim() || undefined,
      });
      await loadVendors();
      applyVendor(targetKey, created);
      setShowVendorQuick(false);
      setVendorTargetKey(null);
      setVendorQuickName('');
      setVendorQuickContact('');
      setVendorQuickPhone('');
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '업체 등록에 실패했습니다.'
      );
    } finally {
      setVendorQuickSaving(false);
    }
  };

  const buildPayload = (sheet: AcceptanceSheetValues): AcceptanceInspectionPayload => ({
    inventoryId: sheet.inventoryId!,
    purchaseOrderId: selectedPurchaseOrderId,
    contractId:
      selectedPurchaseOrderId != null
        ? purchaseOrders.find((po) => po.purchaseOrderId === selectedPurchaseOrderId)
            ?.contractId ?? contractId
        : contractId,
    formTitle: selectedForm!.title,
    authorName: sheet.authorName.trim() || authorNameRef.current || undefined,
    status: 'COMPLETED',
    material: sheet.material.trim() || undefined,
    incomingQty: sheet.incomingQty.trim(),
    inspectorName: sheet.inspectorName.trim() || undefined,
    vendorName: sheet.vendorName.trim() || undefined,
    vendorContactName: sheet.vendorContactName.trim() || undefined,
    vendorPhone: sheet.vendorPhone.trim() || undefined,
    incomingDate: sheet.incomingDate || null,
    inspectionDate: sheet.inspectionDate || null,
    overallJudgment: sheet.overallJudgment.trim(),
    remark: sheet.remark.trim() || undefined,
    items: sheet.items,
  });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedForm) {
      notifyError('사용할 양식을 선택해 주세요.');
      return;
    }

    if (isEdit) {
      const card = cards[0];
      if (!card?.sheet.inventoryId) {
        notifyError('재고 품목을 선택해 주세요.');
        return;
      }
      if (!card.sheet.incomingQty.trim()) {
        notifyError('입고 수량은 필수입니다.');
        return;
      }
      if (!card.sheet.overallJudgment.trim()) {
        notifyError('종합판정은 필수입니다.');
        return;
      }
      try {
        setSaving(true);
        setError('');
        const updated = await updateAcceptanceInspection(
          inspectionId!,
          buildPayload(card.sheet)
        );
        for (const file of card.pendingMediaFiles) {
          await uploadAcceptanceInspectionAttachment(updated.inspectionId, file, 'MEDIA');
        }
        for (const file of card.pendingRemarkFiles) {
          await uploadAcceptanceInspectionAttachment(updated.inspectionId, file, 'REMARK');
        }
        onSaved(updated.inspectionId);
      } catch (err) {
        notifyError(
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            '인수검사 수정에 실패했습니다.'
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    for (let i = 0; i < cards.length; i += 1) {
      const card = cards[i];
      if (!card.sheet.inventoryId) {
        notifyError(`${i + 1}번 성적서: 재고 품목을 선택해 주세요.`);
        patchCard(card.key, { collapsed: false });
        return;
      }
      if (!card.sheet.incomingQty.trim()) {
        notifyError(`${i + 1}번 성적서: 입고 수량은 필수입니다.`);
        patchCard(card.key, { collapsed: false });
        return;
      }
      if (!card.sheet.overallJudgment.trim()) {
        notifyError(`${i + 1}번 성적서: 종합판정은 필수입니다.`);
        patchCard(card.key, { collapsed: false });
        return;
      }
    }

    try {
      setSaving(true);
      setError('');
      const createdIds: number[] = [];
      const failedKeys: string[] = [];
      const lots: string[] = [];

      for (const card of cards) {
        try {
          const created = await createAcceptanceInspection(buildPayload(card.sheet));
          for (const file of card.pendingMediaFiles) {
            await uploadAcceptanceInspectionAttachment(created.inspectionId, file, 'MEDIA');
          }
          for (const file of card.pendingRemarkFiles) {
            await uploadAcceptanceInspectionAttachment(created.inspectionId, file, 'REMARK');
          }
          createdIds.push(created.inspectionId);
          lots.push(created.lotNo || '-');
        } catch {
          failedKeys.push(card.key);
        }
      }

      if (failedKeys.length === 0) {
        window.alert(
          createdIds.length === 1
            ? `Lot No. ${lots[0]}로 등록되었습니다.`
            : `${createdIds.length}건 등록되었습니다.\nLot: ${lots.join(', ')}`
        );
        onSaved(createdIds[createdIds.length - 1]);
        return;
      }

      if (createdIds.length > 0) {
        window.alert(
          `${createdIds.length}건 등록 성공, ${failedKeys.length}건 실패했습니다.\n실패한 성적서만 남겼습니다.`
        );
        setCards((prev) => prev.filter((card) => failedKeys.includes(card.key)));
        reportError(setError, '일부 인수검사 등록에 실패했습니다. 남은 성적서를 확인해 주세요.');
      } else {
        notifyError('인수검사 등록에 실패했습니다.');
      }
    } finally {
      setSaving(false);
    }
  };

  const cardTitle = (card: SheetCard, index: number) => {
    const name = card.sheet.productName?.trim();
    const spec = card.sheet.specText?.trim();
    if (name) return spec ? `${name} / ${spec}` : name;
    return `성적서 ${index + 1}`;
  };

  return (
    <div className="aic">
      <div className="aic__header">
        <div>
          <h3 className="aic__title">{isEdit ? '인수검사 수정' : '인수검사 등록'}</h3>
          <p className="aic__subtitle">
            {isEdit
              ? '성적서를 수정합니다. 다른 사람이 작성한 성적서를 수정하면 검토란에 본인 이름이 기록됩니다.'
              : '발주 품목을 여러 개 고르면 성적서가 여러 장 만들어집니다. 접어서 하나씩 작성하세요.'}
          </p>
        </div>
        <div className="aic__actions">
          <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
            취소
          </button>
        </div>
      </div>

      {error ? <p className="aic__error">{error}</p> : null}
      {loading ? <p className="aic__empty">불러오는 중...</p> : null}

      <PreviousInspectionPickerModal
        open={itemsImportCardKey != null}
        title="이전 검사항목 불러오기"
        subtitle="선택한 인수검사의 검사항목(요구사항·측정값)만 현재 성적서에 채웁니다. 품목·Lot·업체·일자는 유지됩니다. 같은 품목은 위에 표시됩니다."
        emptyText="이전 인수검사가 없습니다."
        loading={loadingPreviousItems}
        pageSize={10}
        priorityIds={itemsImportPriorityIds}
        columns={[
          { key: 'item', label: '품명' },
          { key: 'form', label: '양식' },
          { key: 'lot', label: 'Lot' },
          { key: 'date', label: '검사일' },
          { key: 'status', label: '상태' },
        ]}
        rows={previousRows.map((row) => ({
          id: row.inspectionId,
          cells: {
            item: acceptanceItemLabel(row),
            form: row.formTitle || '-',
            lot: row.lotNo || '-',
            date: row.inspectionDate?.slice(0, 10) || '-',
            status: acceptanceStatusLabel(row.status),
          },
        }))}
        onClose={() => setItemsImportCardKey(null)}
        onSelect={applyPreviousItems}
      />

      <section className="aic__card">
        <div className="aic__card-head">
          <h4 className="aic__step">
            <span className="aic__step-num">0</span>
            발주서 선택
          </h4>
          <p className="aic__hint">선택 시 업체·품목이 자동으로 채워집니다</p>
        </div>
        <div className="aic__form-grid aic__form-grid--po">
          <label className="aic__field">
            발주서
            <select
              value={selectedPurchaseOrderId ?? ''}
              onChange={(e) => void pickPurchaseOrder(e.target.value)}
              disabled={isEdit}
            >
              <option value="">선택 안 함 (직접 입력)</option>
              {sortedPurchaseOrders.map((po) => {
                const status = poAcceptanceStatus(po);
                return (
                  <option
                    key={po.purchaseOrderId}
                    value={po.purchaseOrderId}
                    className={
                      status.tone === 'full'
                        ? 'aic-po-option is-full'
                        : status.tone === 'partial'
                          ? 'aic-po-option is-partial'
                          : 'aic-po-option'
                    }
                  >
                    [{status.label}] {po.orderNo?.trim() || `#${po.purchaseOrderId}`}
                    {po.vendorName ? ` · ${po.vendorName}` : ''}
                    {po.contractName ? ` · ${po.contractName}` : ''}
                  </option>
                );
              })}
            </select>
            {selectedPoAcceptance ? (
              <p className={`aic-po-status is-${selectedPoAcceptance.tone}`}>
                인수검사 {selectedPoAcceptance.label}
                {selectedPoAcceptance.tone === 'full'
                  ? ' · 이미 전 품목이 등록되어 있습니다'
                  : null}
              </p>
            ) : null}
          </label>
        </div>

        {!isEdit && poItems.length > 0 ? (
          <div className="aic-po-items">
            <div className="aic-po-items__head">
              <p className="aic__hint" style={{ margin: 0 }}>
                발주 품목 {poItems.length}개 · 검사할 품목을 선택한 뒤 성적서를 만드세요
              </p>
              <div className="aic-po-items__actions">
                <button
                  type="button"
                  className="aic__btn is-ghost"
                  onClick={() =>
                    setSelectedPoKeys(new Set(poItems.map((item, index) => poItemKey(item, index))))
                  }
                >
                  전체 선택
                </button>
                <button
                  type="button"
                  className="aic__btn is-ghost"
                  onClick={() => setSelectedPoKeys(new Set())}
                >
                  선택 해제
                </button>
                <button
                  type="button"
                  className="aic__btn is-soft"
                  onClick={applyPoSelectionToCards}
                >
                  선택분 성적서 만들기
                </button>
              </div>
            </div>
            <ul className="aic-po-items__list">
              {poItems.map((item, index) => {
                const key = poItemKey(item, index);
                const checked = selectedPoKeys.has(key);
                const alreadyRegistered = selectedPoRegisteredIds.has(item.inventoryId);
                return (
                  <li key={key}>
                    <label
                      className={`aic-po-items__row${checked ? ' is-checked' : ''}${
                        alreadyRegistered ? ' is-registered' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePoItem(key)}
                      />
                      <span className="aic-po-items__name">
                        {inventoryOptionLabel(item.itemName, item.specText)}
                      </span>
                      {alreadyRegistered ? (
                        <span className="aic-po-items__badge">등록됨</span>
                      ) : null}
                      <span className="aic-po-items__meta">
                        수량 {item.orderQty ?? '-'}
                        {item.unit ? ` ${item.unit}` : ''}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>

      <section className="aic__card">
        <div className="aic__card-head">
          <h4 className="aic__step">
            <span className="aic__step-num">1</span>
            양식 선택
          </h4>
          <p className="aic__hint">모든 성적서에 공통으로 적용됩니다</p>
        </div>
        {forms.length === 0 ? (
          <p className="aic__empty">등록된 인수검사 양식이 없습니다.</p>
        ) : (
          <table className="aic__form-table">
            <thead>
              <tr>
                <th style={{ width: 48 }} />
                <th>양식명</th>
                <th>설명</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => (
                <tr
                  key={form.key}
                  className={selectedFormKey === form.key ? 'is-selected' : undefined}
                  onClick={() => setSelectedFormKey(form.key)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <input
                      className="aic__radio"
                      type="radio"
                      name="acceptance-form"
                      checked={selectedFormKey === form.key}
                      onChange={() => setSelectedFormKey(form.key)}
                    />
                  </td>
                  <td>{form.title}</td>
                  <td>{form.memo || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selectedForm ? (
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="aic__footer">
            <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
              취소
            </button>
            <button type="submit" className="aic__btn is-primary" disabled={saving || loading}>
              {saving
                ? '저장 중...'
                : isEdit
                  ? '저장'
                  : cards.length > 1
                    ? `${cards.length}건 일괄 등록`
                    : '저장'}
            </button>
          </div>
          <section className="aic__card">
            <div className="aic__card-head">
              <h4 className="aic__step">
                <span className="aic__step-num">2</span>
                성적서 입력
              </h4>
              {!isEdit ? (
                <button type="button" className="aic__btn is-soft" onClick={addCard}>
                  성적서 추가
                </button>
              ) : null}
            </div>

            {showQuickRegister ? (
              <div className="aic__quick" style={{ marginBottom: 16 }}>
                <div className="aic__card-head" style={{ marginBottom: 8 }}>
                  <h4 className="aic__step" style={{ margin: 0, fontSize: 14 }}>
                    재고 품목 빠른 등록
                  </h4>
                  <button
                    type="button"
                    className="aic__btn is-ghost"
                    onClick={() => {
                      setShowQuickRegister(false);
                      setQuickTargetKey(null);
                    }}
                  >
                    닫기
                  </button>
                </div>
                <div className="aic__quick-grid">
                  <label className="aic__field">
                    유형
                    <select
                      value={quickKind}
                      onChange={(e) => setQuickKind(e.target.value as InventoryItemKind)}
                    >
                      <option value="PART">{inventoryKindLabel('PART')}</option>
                      <option value="MATERIAL">{inventoryKindLabel('MATERIAL')}</option>
                      <option value="FINISHED">{inventoryKindLabel('FINISHED')}</option>
                    </select>
                  </label>
                  <label className="aic__field">
                    품명
                    <input
                      value={quickName}
                      onChange={(e) => setQuickName(e.target.value)}
                      placeholder="예: LED 모듈"
                    />
                  </label>
                  <label className="aic__field">
                    규격
                    <input
                      value={quickSpec}
                      onChange={(e) => setQuickSpec(e.target.value)}
                      placeholder="예: 5mm"
                    />
                  </label>
                  <label className="aic__field">
                    단위
                    <input
                      value={quickUnit}
                      onChange={(e) => setQuickUnit(e.target.value)}
                      placeholder="ea"
                    />
                  </label>
                  <button
                    type="button"
                    className="aic__btn is-primary"
                    onClick={() => void handleQuickRegister()}
                    disabled={quickSaving}
                  >
                    {quickSaving ? '등록 중...' : '등록 후 선택'}
                  </button>
                </div>
              </div>
            ) : null}

            {showVendorQuick ? (
              <div className="aic__quick" style={{ marginBottom: 16 }}>
                <div className="aic__card-head" style={{ marginBottom: 8 }}>
                  <h4 className="aic__step" style={{ margin: 0, fontSize: 14 }}>
                    공급업체 빠른 등록
                  </h4>
                  <button
                    type="button"
                    className="aic__btn is-ghost"
                    onClick={() => {
                      setShowVendorQuick(false);
                      setVendorTargetKey(null);
                    }}
                  >
                    닫기
                  </button>
                </div>
                <div className="aic__quick-grid">
                  <label className="aic__field">
                    업체명
                    <input
                      value={vendorQuickName}
                      onChange={(e) => setVendorQuickName(e.target.value)}
                      placeholder="예: ○○전자"
                    />
                  </label>
                  <label className="aic__field">
                    담당자
                    <input
                      value={vendorQuickContact}
                      onChange={(e) => setVendorQuickContact(e.target.value)}
                      placeholder="담당자명"
                    />
                  </label>
                  <label className="aic__field">
                    연락처
                    <input
                      value={vendorQuickPhone}
                      onChange={(e) => setVendorQuickPhone(e.target.value)}
                      placeholder="010-0000-0000"
                    />
                  </label>
                  <button
                    type="button"
                    className="aic__btn is-primary"
                    onClick={() => void handleVendorQuickRegister()}
                    disabled={vendorQuickSaving}
                  >
                    {vendorQuickSaving ? '등록 중...' : '등록 후 적용'}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="aic-sheet-list">
              {cards.map((card, index) => (
                <div
                  key={card.key}
                  className={`aic-sheet-card${card.collapsed ? ' is-collapsed' : ''}`}
                >
                  <div className="aic-sheet-card__head">
                    <button
                      type="button"
                      className="aic-sheet-card__toggle"
                      onClick={() => patchCard(card.key, { collapsed: !card.collapsed })}
                    >
                      <span className="aic-sheet-card__chevron" aria-hidden>
                        {card.collapsed ? '▸' : '▾'}
                      </span>
                      <span className="aic-sheet-card__title">
                        {index + 1}. {cardTitle(card, index)}
                      </span>
                    </button>
                    <div className="aic-sheet-card__actions">
                      <button
                        type="button"
                        className="aic__btn is-ghost"
                        onClick={() => setItemsImportCardKey(card.key)}
                      >
                        이전 검사항목 불러오기
                      </button>
                      {!isEdit ? (
                        <button
                          type="button"
                          className="aic__btn is-ghost"
                          onClick={() => removeCard(card.key)}
                        >
                          삭제
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {!card.collapsed ? (
                    <div className="aic-sheet-card__body">
                      <AcceptanceInspectionSheet
                        editable
                        values={card.sheet}
                        lotNoHint={lotNoHint}
                        inventoryOptions={inventoryOptions.map((row) => ({
                          inventoryId: row.inventoryId,
                          itemKind: row.itemKind,
                          itemName: row.itemName,
                          specText: row.specText,
                        }))}
                        vendorOptions={vendors}
                        mediaAttachments={card.savedAttachments.filter(isMediaAttachment)}
                        remarkAttachments={card.savedAttachments.filter(isRemarkAttachment)}
                        pendingMediaFiles={card.pendingMediaFiles}
                        pendingRemarkFiles={card.pendingRemarkFiles}
                        pendingMediaPreviewUrls={mediaPreviewByCard[card.key] || {}}
                        onChange={(patch) => patchCardSheet(card.key, patch)}
                        onItemChange={(itemIndex, patch) =>
                          updateItem(card.key, itemIndex, patch)
                        }
                        onAddItem={() => addItem(card.key)}
                        onRemoveItem={(itemIndex) => removeItem(card.key, itemIndex)}
                        onPickMediaFiles={(files) =>
                          patchCard(card.key, {
                            pendingMediaFiles: [...card.pendingMediaFiles, ...files],
                          })
                        }
                        onPickRemarkFiles={(files) =>
                          patchCard(card.key, {
                            pendingRemarkFiles: [...card.pendingRemarkFiles, ...files],
                          })
                        }
                        onRemovePendingMediaFile={(fileIndex) =>
                          patchCard(card.key, {
                            pendingMediaFiles: card.pendingMediaFiles.filter(
                              (_, i) => i !== fileIndex
                            ),
                          })
                        }
                        onRemovePendingRemarkFile={(fileIndex) =>
                          patchCard(card.key, {
                            pendingRemarkFiles: card.pendingRemarkFiles.filter(
                              (_, i) => i !== fileIndex
                            ),
                          })
                        }
                        onDeleteAttachment={(file) => {
                          if (!isEdit || inspectionId == null) return;
                          if (!window.confirm(`"${file.fileName}" 첨부를 삭제할까요?`)) return;
                          void deleteAcceptanceInspectionAttachment(
                            inspectionId,
                            file.attachmentId
                          )
                            .then(() =>
                              patchCard(card.key, {
                                savedAttachments: card.savedAttachments.filter(
                                  (row) => row.attachmentId !== file.attachmentId
                                ),
                              })
                            )
                            .catch(() =>
                              reportError(setError, '첨부파일 삭제에 실패했습니다.')
                            );
                        }}
                        onOpenQuickRegister={() => {
                          setQuickTargetKey(card.key);
                          setShowQuickRegister(true);
                        }}
                        onPickVendor={(vendorId) => {
                          const vendor = vendors.find((v) => v.vendorId === vendorId);
                          if (vendor) applyVendor(card.key, vendor);
                        }}
                        onOpenVendorQuickRegister={() => {
                          setVendorTargetKey(card.key);
                          setShowVendorQuick(true);
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="aic__footer">
              <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
                취소
              </button>
              <button type="submit" className="aic__btn is-primary" disabled={saving || loading}>
                {saving
                  ? '저장 중...'
                  : isEdit
                    ? '저장'
                    : cards.length > 1
                      ? `${cards.length}건 일괄 등록`
                      : '저장'}
              </button>
            </div>
          </section>
        </form>
      ) : (
        <p className="aic__empty">양식을 선택하면 성적서 입력 화면이 표시됩니다.</p>
      )}
    </div>
  );
}

export default AcceptanceInspectionCreate;
